/*
 * ET - Collection FAQ (sections/et-collection-faq.liquid)
 * Progressive enhancement for the <details> accordion: smooth height animation, aria-expanded kept in sync,
 * optional "only one open at a time", and opening the selected block in the theme editor.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-et-collection-faq]';
  var ITEM_SELECTOR = '.et-collection-faq__item';
  var QUESTION_SELECTOR = '.et-collection-faq__question';
  var READY_ATTRIBUTE = 'data-et-collection-faq-ready';
  var DURATION = 300;
  var EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

  var controllers = new Map();
  var animations = new WeakMap();
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function getQuestion(item) {
    var first = item.firstElementChild;
    return first && first.matches(QUESTION_SELECTOR) ? first : null;
  }

  function cancelAnimation(item) {
    var animation = animations.get(item);
    if (!animation) return;
    animations.delete(item);
    animation.cancel();
  }

  function animateHeight(item, from, to, onDone) {
    var canAnimate = typeof item.animate === 'function' && !(reducedMotion && reducedMotion.matches) && from !== to;

    if (!canAnimate) {
      onDone();
      return;
    }

    var animation = item.animate(
      { height: [from + 'px', to + 'px'] },
      { duration: DURATION, easing: EASING, fill: 'forwards' }
    );

    animations.set(item, animation);

    animation.onfinish = function () {
      if (animations.get(item) !== animation) return;
      animations.delete(item);
      onDone();
      animation.cancel();
    };
  }

  function Accordion(root) {
    this.root = root;
    this.single = root.hasAttribute('data-et-collection-faq-single');
    this.handleClick = this.handleClick.bind(this);
    this.handleToggle = this.handleToggle.bind(this);

    // The native `name` exclusivity closes siblings instantly; the script takes over so they animate too.
    if (this.single) {
      this.getItems().forEach(function (item) {
        item.removeAttribute('name');
      });
    }

    this.getItems().forEach(function (item) {
      var question = getQuestion(item);
      if (question) question.setAttribute('aria-expanded', item.open ? 'true' : 'false');
    });

    root.addEventListener('click', this.handleClick);
    root.addEventListener('toggle', this.handleToggle, true);
  }

  Accordion.prototype.getItems = function () {
    return Array.prototype.slice.call(this.root.querySelectorAll(ITEM_SELECTOR));
  };

  Accordion.prototype.isExpanded = function (item) {
    var question = getQuestion(item);
    return question ? question.getAttribute('aria-expanded') === 'true' : item.open;
  };

  Accordion.prototype.open = function (item) {
    var question = getQuestion(item);
    var from = item.offsetHeight;

    cancelAnimation(item);
    item.open = true;
    if (question) question.setAttribute('aria-expanded', 'true');

    var to = item.offsetHeight;
    animateHeight(item, from, to, function () {});

    if (this.single) this.closeOthers(item);
  };

  Accordion.prototype.close = function (item) {
    var question = getQuestion(item);
    if (!question) return;

    var from = item.offsetHeight;

    cancelAnimation(item);
    question.setAttribute('aria-expanded', 'false');

    var to = question.offsetHeight;
    animateHeight(item, from, to, function () {
      item.open = false;
    });
  };

  Accordion.prototype.closeOthers = function (current) {
    var self = this;
    this.getItems().forEach(function (item) {
      if (item !== current && self.isExpanded(item)) self.close(item);
    });
  };

  Accordion.prototype.handleClick = function (event) {
    var question = event.target.closest(QUESTION_SELECTOR);
    if (!question || !this.root.contains(question)) return;

    var item = question.parentElement;
    if (!item || !item.matches(ITEM_SELECTOR)) return;

    event.preventDefault();

    if (this.isExpanded(item)) {
      this.close(item);
    } else {
      this.open(item);
    }
  };

  // Native toggles (find-in-page, editor block select): keep aria-expanded and exclusivity in sync.
  Accordion.prototype.handleToggle = function (event) {
    var item = event.target;
    if (!item.matches || !item.matches(ITEM_SELECTOR) || animations.has(item)) return;

    var question = getQuestion(item);
    if (question) question.setAttribute('aria-expanded', item.open ? 'true' : 'false');

    if (item.open && this.single) this.closeOthers(item);
  };

  Accordion.prototype.openItem = function (item) {
    if (!this.isExpanded(item)) this.open(item);
  };

  Accordion.prototype.destroy = function () {
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('toggle', this.handleToggle, true);
    this.getItems().forEach(cancelAnimation);
  };

  function init(scope) {
    var container = scope || document;
    var roots = container.matches && container.matches(ROOT_SELECTOR) ? [container] : [];
    roots = roots.concat(Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR)));

    roots.forEach(function (root) {
      // The DOM marker also stops a second copy of this file (two FAQ sections on one page) binding twice.
      if (controllers.has(root) || root.hasAttribute(READY_ATTRIBUTE)) return;
      root.setAttribute(READY_ATTRIBUTE, '');
      controllers.set(root, new Accordion(root));
    });
  }

  function destroy(scope) {
    controllers.forEach(function (controller, root) {
      if (scope === root || scope.contains(root)) {
        controller.destroy();
        controllers.delete(root);
        root.removeAttribute(READY_ATTRIBUTE);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target;
    if (!item || !item.matches || !item.matches(ITEM_SELECTOR)) return;

    var root = item.closest(ROOT_SELECTOR);
    var controller = root ? controllers.get(root) : null;
    if (controller) controller.openItem(item);
  });
})();
